import React, { createContext, useCallback, useContext, useMemo } from 'react';
import type { ViewProps } from 'react-native';

import { getEnvVars } from '@order/utils';

import {
  initOneSignal,
  OneSignal as client,
  resetOneSignalUserSession as resetCustomer,
  startOneSignalUserSession as startCustomerSession,
} from './client';

const { DISABLE_ONESIGNAL_PROMPT } = getEnvVars();

// ────────────────────────────────────────────────────────────────────────────────

const OneSignalContext = createContext<OneSignalContextType | undefined>(
  undefined,
);

/**
 * One Signal (@see https://www.onesignal.com/) provider component.
 */
export const OneSignalProvider = (props: Pick<ViewProps, 'children'>) => {
  //
  // ─── HELPERS ────────────────────────────────────────────────────────

  const init = useCallback(() => {
    (async () => {
      if (DISABLE_ONESIGNAL_PROMPT) return;
      await initOneSignal();
    })();
  }, []);

  const syncCustomer = useCallback((customer: OneSignalUser) => {
    (async () => {
      if (!customer.id) return;
      await startCustomerSession(customer);
    })();
  }, []);

  // ────────────────────────────────────────────────────────────────────

  const value = useMemo(
    () => ({ client, init, syncCustomer, resetCustomer }),
    [init, syncCustomer],
  );

  return (
    <OneSignalContext.Provider value={value}>
      {props.children}
    </OneSignalContext.Provider>
  );
};

/**
 * One Signal (@see https://www.onesignal.com/) client hook.
 * Returns platform specific client and initialized state boolean for future use.
 *
 * ⚠️ Note that the clients APIs can have some differences in case of Web and React Native!
 *    You can use abstract wrapper functions to handle those cases.
 *
 * @example
 * const { client as oneSignalClient } = useOneSignal();
 * const oneSignalUserId = oneSignalClient.getExternalUserId();
 */
export const useOneSignal = () => {
  const context = useContext(OneSignalContext);

  if (context === undefined) {
    throw new Error('useOneSignal must be used within a <OneSignalProvider>');
  }

  return context;
};

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type OneSignalContextType = {
  readonly client: typeof client;
  readonly init: () => void;
  readonly syncCustomer: (customer: OneSignalUser) => void;
  readonly resetCustomer: () => void;
};

type OneSignalUser = {
  readonly id?: string;
  readonly email?: string;
};
